import {Inject, Injectable} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {AppState} from '../../core/store/app.state';
import {Store} from '@ngrx/store';
import {getCountNotification} from '../../core/store/notification/notification.selectors';
import {getCountConversations} from '../../core/store/conversation/conversation.selectors';
import {SsrContext} from './util/ssr-context.service';
import { DOCUMENT } from '@angular/common';

export interface BreadcrumbItem {
  name: string;
  url: string;
}

@Injectable({providedIn: 'root'})
export class SEOService {
  nbNotif = 0;
  notifCount = 0;
  messageCount = 0;
  titleSEO = '';
  favIcon32: HTMLLinkElement;
  favIcon16: HTMLLinkElement;

  constructor(private title: Title,
              private meta: Meta,
              private store: Store<AppState>,
              private ssrContext: SsrContext,
              @Inject(DOCUMENT) private dom,
              private router: Router) {
    if (!this.ssrContext.isServer()) {
      this.favIcon32 = document.querySelector('#appIcon32');
      this.favIcon16 = document.querySelector('#appIcon16');
    }
  }

  updateSEO(title: string, desc: string, img?: string): void {
    this.titleSEO = title.substring(0, 62) + ' • Tooda';
    const descSEO = desc.substring(0, 159);

    this.changeTitleWithCount();
    this.meta.updateTag({name: 'description', content: descSEO});

    // Open Graph amélioré
    this.meta.updateTag({property: 'og:type', content: 'website'});
    this.meta.updateTag({property: 'og:url', content: 'https://tooda.fr' + this.router.url});
    this.meta.updateTag({property: 'og:title', content: this.titleSEO});
    this.meta.updateTag({property: 'og:description', content: descSEO});
    this.meta.updateTag({property: 'og:image', content: img ?? 'https://tooda.fr/assets/images/tooda_logo_og.png'});
    this.meta.updateTag({property: 'og:image:width', content: '1200'});
    this.meta.updateTag({property: 'og:image:height', content: '630'});
    this.meta.updateTag({property: 'og:site_name', content: 'Tooda'});
    this.meta.updateTag({property: 'og:locale', content: 'fr_FR'});

    // Twitter Cards amélioré
    this.meta.updateTag({name: 'twitter:card', content: 'summary_large_image'});
    this.meta.updateTag({name: 'twitter:title', content: this.titleSEO});
    this.meta.updateTag({name: 'twitter:site', content: '@tooda_fr'});
    this.meta.updateTag({name: 'twitter:creator', content: '@tooda_fr'});
    this.meta.updateTag({name: 'twitter:description', content: descSEO});
    this.meta.updateTag({name: 'twitter:url', content: 'https://tooda.fr' + this.router.url});
    this.meta.updateTag({name: 'twitter:image', content: img ?? 'https://tooda.fr/assets/images/tooda_logo_og.png'});

    // Métadonnées supplémentaires pour le SEO
    this.meta.updateTag({name: 'robots', content: 'index, follow'});
    this.meta.updateTag({name: 'author', content: 'Tooda'});
    this.meta.updateTag({name: 'language', content: 'fr'});
    this.meta.updateTag({name: 'theme-color', content: '#602719'});

    this.store.select(getCountNotification).subscribe(count => {
      this.notifCount = count;
      this.nbNotif = this.notifCount + this.messageCount;

      this.changeTitleWithCount();
    });

    this.store.select(getCountConversations).subscribe(count => {
      this.messageCount = count;
      this.nbNotif = this.notifCount + this.messageCount;

      this.changeTitleWithCount();
    });

    this.updateCanonicalUrl('https://tooda.fr' + this.router.url);
  }

  // Nouvelle méthode pour les pages de recettes
  updateRecipeSEO(recipe: any): void {
    const title = `${recipe.title} - Recette ${recipe.category.typeLabel}`;
    const description = `Découvrez la recette ${recipe.title} sur Tooda. ${recipe.description.substring(0, 100)}...`;
    const image = recipe.imagesUrl.length > 0 ? recipe.imagesUrl[0].url : undefined;
    
    this.updateSEO(title, description, image);
    
    // Métadonnées spécifiques aux recettes
    this.meta.updateTag({property: 'og:type', content: 'article'});
    this.meta.updateTag({name: 'article:author', content: recipe.username});
    this.meta.updateTag({name: 'article:published_time', content: recipe.createdDate});
    this.meta.updateTag({name: 'article:section', content: recipe.category.typeLabel});
    
    // Tags pour les ingrédients
    recipe.tags.forEach((tag: any) => {
      this.meta.addTag({name: 'article:tag', content: tag.valueLabel});
    });

    // Ajouter les breadcrumbs structurés
    const breadcrumbs = this.generateRecipeBreadcrumbs(recipe);
    this.addBreadcrumbSchema(breadcrumbs);
  }

  // Nouvelle méthode pour les pages de blog
  updateBlogSEO(blog: any): void {
    this.updateSEO(blog.title, blog.metaDescription, blog.imageUrl);
    
    // Métadonnées spécifiques aux articles de blog
    this.meta.updateTag({property: 'og:type', content: 'article'});
    this.meta.updateTag({name: 'article:author', content: 'Alexandra'});
    this.meta.updateTag({name: 'article:published_time', content: blog.createdDate});
    this.meta.updateTag({name: 'article:modified_time', content: blog.lastModifiedDate});
    this.meta.updateTag({name: 'article:section', content: blog.category});

    // Ajouter les breadcrumbs structurés
    const breadcrumbs = this.generateBlogBreadcrumbs(blog);
    this.addBreadcrumbSchema(breadcrumbs);
  }

  // Nouvelle méthode pour les pages d'ingrédients
  updateIngredientSEO(ingredient: any): void {
    const title = `${ingredient.name} - Guide complet de l'ingrédient`;
    const description = `Tout savoir sur ${ingredient.name} : propriétés, utilisations, recettes. Guide complet sur Tooda.`;
    
    this.updateSEO(title, description);
    
    // Métadonnées spécifiques aux ingrédients
    this.meta.updateTag({name: 'keywords', content: `${ingredient.name}, ingrédient, cosmétique, DIY, fait maison`});

    // Ajouter les breadcrumbs structurés
    const breadcrumbs = this.generateIngredientBreadcrumbs(ingredient);
    this.addBreadcrumbSchema(breadcrumbs);
  }

  // Nouvelle méthode pour les pages simples (connexion, inscription, etc.)
  updatePageSEO(title: string, description: string, pageName: string, img?: string): void {
    this.updateSEO(title, description, img);
    
    // Ajouter les breadcrumbs simples
    const breadcrumbs = this.generateSimpleBreadcrumbs(pageName);
    this.addBreadcrumbSchema(breadcrumbs);
  }

  // Nouvelle méthode pour les pages de liste de recettes
  updateRecipeListSEO(title: string, description: string, category: any, img?: string): void {
    this.updateSEO(title, description, img);
    
    // Ajouter les breadcrumbs pour les listes de recettes
    const breadcrumbs = this.generateRecipeListBreadcrumbs(category);
    this.addBreadcrumbSchema(breadcrumbs);
  }

  updateCanonicalUrl(url: string): void {
    const head = this.dom.getElementsByTagName('head')[0];
    let element: HTMLLinkElement = this.dom.querySelector(`link[rel='canonical']`) || null;
    if (element == null) {
      element = this.dom.createElement('link') as HTMLLinkElement;
      head.appendChild(element);
    }
    element.setAttribute('rel', 'canonical');
    element.setAttribute('href', url);
  }

  private changeTitleWithCount(): void {
    if (this.nbNotif > 0) {
      const nbNotifTitle = this.nbNotif > 99 ? '99+' : this.nbNotif;
      this.title.setTitle('(' + nbNotifTitle + ') ' + this.titleSEO);
      if (this.favIcon32 && this.favIcon16) {
        this.favIcon32.href = '/assets/favicon-32x32-notif.png';
        this.favIcon16.href = '/assets/favicon-16x16-notif.png';
      }
    } else {
      this.title.setTitle(this.titleSEO);
      if (this.favIcon32 && this.favIcon16) {
        this.favIcon32.href = '/assets/favicon-32x32.png';
        this.favIcon16.href = '/assets/favicon-16x16.png';
      }
    }
  }

  // Nouvelle méthode pour ajouter les breadcrumbs structurés
  addBreadcrumbSchema(breadcrumbs: BreadcrumbItem[]): void {
    // Remove any existing breadcrumb schema
    const existingScripts = this.dom.querySelectorAll('script[type="application/ld+json"][data-breadcrumb-schema]');
    existingScripts.forEach((script: any) => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    });

    if (breadcrumbs.length === 0) return;

    const schema = {
      '@context': 'https://schema.org/',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url.startsWith('http') ? item.url : `https://tooda.fr${item.url}`
      }))
    };

    // Create and inject the breadcrumb JSON-LD script tag
    const script = this.dom.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-breadcrumb-schema', 'true');
    const scriptContent = document.createTextNode(JSON.stringify(schema));
    script.appendChild(scriptContent);
    this.dom.head.appendChild(script);
  }

  // Méthode pour nettoyer les breadcrumbs (à appeler dans ngOnDestroy)
  removeBreadcrumbSchema(): void {
    if (!this.ssrContext.isServer()) {
      const existingScripts = this.dom.querySelectorAll('script[type="application/ld+json"][data-breadcrumb-schema]');
      existingScripts.forEach((script: any) => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    }
  }

  // Méthodes helper pour générer des breadcrumbs communs
  generateRecipeBreadcrumbs(recipe: any): BreadcrumbItem[] {
    return [
      { name: 'Accueil', url: '/' },
      { name: 'Recettes', url: '/recette/liste' },
      { name: recipe.domain.label, url: `/recette/liste/${recipe.domain.title}` },
      { name: recipe.category.typeLabel, url: `/recette/liste/${recipe.domain.title}?categorie=${recipe.category.type}` },
      { name: recipe.category.subCategoryLabel, url: `/recette/liste/${recipe.domain.title}?categorie=${recipe.category.type}&sousCategorie=${recipe.category.subCategory}` },
      { name: recipe.title, url: this.router.url }
    ];
  }

  generateBlogBreadcrumbs(blog: any): BreadcrumbItem[] {
    return [
      { name: 'Accueil', url: '/' },
      { name: 'Blog', url: '/blog/liste' },
      { name: blog.category, url: `/blog/liste?categorie=${blog.categoryId}` },
      { name: blog.title, url: this.router.url }
    ];
  }

  generateIngredientBreadcrumbs(ingredient: any): BreadcrumbItem[] {
    return [
      { name: 'Accueil', url: '/' },
      { name: 'Répertoire des ingrédients', url: '/ingredient' },
      { name: ingredient.name, url: this.router.url }
    ];
  }

  generateSimpleBreadcrumbs(pageName: string): BreadcrumbItem[] {
    return [
      { name: 'Accueil', url: '/' },
      { name: pageName, url: this.router.url }
    ];
  }

  generateRecipeListBreadcrumbs(category: any): BreadcrumbItem[] {
    const breadcrumbs = [
      { name: 'Accueil', url: '/' },
      { name: 'Recettes', url: '/recette/liste' }
    ];

    if (category && category.domain) {
      breadcrumbs.push({ name: category.domain, url: `/recette/liste/${category.domainTitle || category.domain.toLowerCase()}` });
      
      if (category.typeLabel) {
        breadcrumbs.push({ name: category.typeLabel, url: `/recette/liste/${category.domainTitle || category.domain.toLowerCase()}?categorie=${category.type}` });
        
        if (category.subCategoryLabel) {
          breadcrumbs.push({ name: category.subCategoryLabel, url: this.router.url });
        }
      }
    }

    return breadcrumbs;
  }
}
